import Descriptor from "./Descriptor";
import UnitType, { distributeUnitsToGroups, UnitGroups, UnitGroup } from "./UnitType";

/** Maximum number of standard-size entries allowed per type. */
export const MAX_STANDARD_SIZES_PER_TYPE = 9;

export interface StandardSizeDefinition {
  label: string;
  minArea: number;
  maxArea: number;
  /** Optional type: 'hardstand'. Absent means regular storage. */
  type?: string;
}

export interface StandardSizeTable {
  units: StandardSizeDefinition[];
}

export interface StandardSizeMatch {
  definition: StandardSizeDefinition;
  unitTypes: UnitType[];
  unitGroups: UnitGroups;
  descriptor?: Descriptor;
  /** Parent descriptor per candidate unit type (floor groups may differ). */
  descriptorByUnitTypeId: Map<string, Descriptor>;
  bestGroup: UnitGroup | null;
  available: boolean;
}

/**
 * Classify a unit type into a standard-size type category.
 *
 * - `'hardstand'` – typeName contains "Carpark" or "Secure Parking" (case-insensitive)
 * - `''`          – regular storage (default)
 */
export function classifyUnitType(ut: UnitType): string {
  if (/carpark|secure\s+parking/i.test(ut.typeName)) return "hardstand";
  return "";
}

/**
 * Validates that standard size definitions have non-overlapping, ascending area ranges.
 * Enforces: min of row N >= max of row N-1 (with small epsilon for float comparison).
 */
export function validateStandardSizes(
  sizes: StandardSizeDefinition[]
): StandardSizeDefinition[] {
  return sizes.filter((s) => s.maxArea > s.minArea).sort((a, b) => a.minArea - b.minArea);
}

/**
 * Given all descriptors and all unit types from the API plus the standard
 * size definitions, produces a StandardSizeMatch for each definition.
 *
 * @param descriptors - Visible descriptors from the unit list API response
 * @param allUnitTypes - ALL unit types from the API (includes those with inline descriptors)
 * @param definitions - The standard size table rows
 * @param groundFloor - The facility's ground floor setting for floor grouping
 */
export function matchStandardSizes(
  descriptors: Descriptor[],
  allUnitTypes: UnitType[],
  definitions: StandardSizeDefinition[],
  groundFloor: number,
  preferFn?: (ut: UnitType) => boolean
): StandardSizeMatch[] {
  // Validate, sort, then enforce per-type limit.
  const validated = validateStandardSizes(definitions);
  const typeCounts: Record<string, number> = {};
  const sorted = validated.filter((d) => {
    const t = !d.type || d.type === "default" ? "" : d.type;
    typeCounts[t] = (typeCounts[t] || 0) + 1;
    return typeCounts[t] <= MAX_STANDARD_SIZES_PER_TYPE;
  });

  // Filter out hidden or disabled descriptors so their unit types are excluded.
  const visibleDescriptors = descriptors.filter((d) => !d.hidden && d.enabled);

  const descriptorById = new Map<string, Descriptor>();
  for (const d of visibleDescriptors) {
    descriptorById.set(d._id, d);
  }

  // Build a set of valid unit type IDs from visible descriptors so we only
  // consider unit types that belong to a visible descriptor (defense-in-depth).
  const validUnitTypeIds = new Set<string>();
  for (const d of visibleDescriptors) {
    for (const ut of d.unitTypes) {
      validUnitTypeIds.add(ut._id);
    }
  }

  // Only use unit types listed inside visible descriptors
  const visibleUnitTypes = allUnitTypes.filter((ut) => validUnitTypeIds.has(ut._id));

  // Map each unit type to its parent descriptor (name, description, picture, etc.).
  // Unit types from the unit-list API are plain objects without descriptorId;
  // they only appear nested under descriptors[].unitTypes.
  const descriptorByUnitTypeId = new Map<string, Descriptor>();
  for (const d of visibleDescriptors) {
    for (const ut of d.unitTypes) {
      descriptorByUnitTypeId.set(ut._id, d);
    }
  }
  for (const ut of visibleUnitTypes) {
    const utWithDescriptor = ut as UnitType & {
      descriptor?: Descriptor;
      descriptorId?: string;
    };
    if (utWithDescriptor.descriptor) {
      descriptorByUnitTypeId.set(ut._id, utWithDescriptor.descriptor);
    } else if (
      utWithDescriptor.descriptorId &&
      descriptorById.has(utWithDescriptor.descriptorId)
    ) {
      const d = descriptorById.get(utWithDescriptor.descriptorId);
      if (d) descriptorByUnitTypeId.set(ut._id, d);
    }
  }

  return sorted.map((definition): StandardSizeMatch => {
    // Normalise: "default", "" and undefined all mean regular storage.
    const defType =
      !definition.type || definition.type === "default" ? "" : definition.type;
    const candidateUnitTypes: UnitType[] = [];

    for (const unitType of visibleUnitTypes) {
      if (classifyUnitType(unitType) !== defType) continue;
      if (unitType.area >= definition.minArea && unitType.area <= definition.maxArea) {
        candidateUnitTypes.push(unitType);
      }
    }

    const unitGroups = distributeUnitsToGroups(candidateUnitTypes, groundFloor, preferFn);
    const bestGroup = pickBestGroup(unitGroups);
    const bestUnitType = bestGroup ? unitGroups[bestGroup] : null;

    const descriptorByUnitTypeIdForMatch = new Map<string, Descriptor>();
    for (const ut of candidateUnitTypes) {
      let d = descriptorByUnitTypeId.get(ut._id);
      if (!d) {
        const utWith = ut as UnitType & {
          descriptor?: Descriptor;
          descriptorId?: string;
        };
        if (utWith.descriptor) {
          d = utWith.descriptor;
        } else if (utWith.descriptorId && descriptorById.has(utWith.descriptorId)) {
          d = descriptorById.get(utWith.descriptorId);
        }
      }
      descriptorByUnitTypeIdForMatch.set(
        ut._id,
        d ?? syntheticDescriptorFromUnitType(ut)
      );
    }

    let descriptor: Descriptor | undefined;
    if (bestUnitType) {
      descriptor = descriptorByUnitTypeIdForMatch.get(bestUnitType._id);
    }

    const available = candidateUnitTypes.some((u) => u.totalAvailable > 0);
    return {
      definition,
      unitTypes: candidateUnitTypes,
      unitGroups,
      descriptor,
      descriptorByUnitTypeId: descriptorByUnitTypeIdForMatch,
      bestGroup,
      available,
    };
  });
}

/**
 * Picks the best floor group: prefers groups with available units,
 * then lowest price among available, then lowest price among unavailable.
 */
function syntheticDescriptorFromUnitType(ut: UnitType): Descriptor {
  return {
    _id: `synthetic-${ut._id}`,
    locationCode: ut.locationCode,
    corpCode: ut.corpCode,
    name: ut.typeName,
    description: "",
    picture: ut.image,
    slides: [],
    defaultInsuranceCoverage: null,
    deals: [],
    enabled: true,
    hidden: false,
    unitTypes: [ut],
    ordinalPosition: 0,
    area: ut.area,
  };
}

function pickBestGroup(unitGroups: UnitGroups): UnitGroup | null {
  const entries = (Object.entries(unitGroups) as [UnitGroup, UnitType | null][]).filter(
    ([, type]) => type !== null
  );

  if (entries.length === 0) return null;

  entries.sort(([, a], [, b]) => {
    if (a === null || b === null) return a === null ? 1 : -1;
    if (!!a.totalAvailable !== !!b.totalAvailable) {
      return a.totalAvailable ? -1 : 1;
    }
    return a.rates.discountedRate - b.rates.discountedRate;
  });

  return entries[0][0];
}
